// Client-side image preparation for the AI Worksheet Marker.
// Compresses large photos to keep upload size within the backend request limit.

const MAX_IMAGE_EDGE = 1900;
const JPEG_QUALITY = 0.86;

export function bytesToHuman(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const power = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** power).toFixed(power === 0 ? 0 : 1)} ${units[power]}`;
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`Unable to read ${file.name}.`));
    reader.onload = () => resolve(String(reader.result));
    reader.readAsDataURL(file);
  });
}

function imageFromDataUrl(dataUrl) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("The selected image could not be opened."));
    image.src = dataUrl;
  });
}

export async function prepareUpload(file) {
  if (!file.type.startsWith("image/")) {
    throw new Error(`${file.name} is not a supported image. Please upload JPG or PNG.`);
  }

  const sourceDataUrl = await fileToDataUrl(file);
  const image = await imageFromDataUrl(sourceDataUrl);
  const scale = Math.min(1, MAX_IMAGE_EDGE / Math.max(image.naturalWidth, image.naturalHeight));
  const width = Math.max(1, Math.round(image.naturalWidth * scale));
  const height = Math.max(1, Math.round(image.naturalHeight * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d", { alpha: false });
  context.fillStyle = "white";
  context.fillRect(0, 0, width, height);
  context.drawImage(image, 0, 0, width, height);
  const compressed = canvas.toDataURL("image/jpeg", JPEG_QUALITY);

  return {
    id: crypto.randomUUID(),
    name: file.name.replace(/\.[^.]+$/, ".jpg"),
    mimeType: "image/jpeg",
    size: Math.round((compressed.length * 3) / 4),
    dataUrl: compressed,
    previewUrl: compressed,
  };
}

export function approximateJsonBytes(value) {
  return new Blob([JSON.stringify(value)]).size;
}
